/* eslint-disable no-param-reassign */
import fetchOffers, { type ListOffer } from '~/lib/goliath/offers';
import type { OfferFilterParams } from '~/lib/goliath/goliath.types';
import type { CardComponentProps } from '@i22/td-longtail-vue-components/dist/components/card/card.vue';
import { getMainImageFromOffer } from '~/lib/goliath-media';
import { getActivePromotions } from '~/lib/promotions';
import { captureException } from '~/lib/sentry';

export type Promotion<P = Record<string, any>> = {
  articleNumber?: string;
  tileGroups?: (string | number)[];
  showWithoutOffer?: boolean;
  /**
   * If it's a string, use this format: "DD.MM.YYYY", or "DD.MM.YYYY HH:mm"
   */
  startDate?: string | Date;
  /**
   * ⚠️ Remember! Without time, "00:00" is used, so an endDate "15.04" will be "14.04. 23:59"!
   */
  endDate?: string | Date; // Date String is "DD.MM.YYYY", or "DD.MM.YYYY HH:mm"
} & P;

export type CardPromotion<P = Promotion> = {
  card: CardComponentProps;
  imageNumber?: number | undefined; // select image from goliath
} & Promotion<P>;

export type PromotionOffer<P = Promotion, O = ListOffer> = { offer?: O | undefined } & Promotion<P>;

export const getOffersByGroup = <P = Promotion, O = ListOffer>(
  promotionOffers: PromotionOffer<P, O>[] | null,
  group: string | number,
  count: number | undefined = undefined
): PromotionOffer<P, O>[] => {
  const offerGroup = promotionOffers?.filter(({ tileGroups }) => tileGroups?.includes(group)) || [];
  return count ? offerGroup.slice(0, count) : offerGroup;
};

export const getCardOffersByGroup = (
  promotionOffers: PromotionOffer<CardPromotion>[] | null,
  group: string | number,
  count: number | undefined = undefined
): PromotionOffer<CardPromotion>[] =>
  getOffersByGroup(promotionOffers, group, count).map((promotion) => {
    if (!promotion.offer) return promotion;
    if (promotion.card.heading && !promotion.card.heading.text) {
      promotion.card.heading.text = promotion.offer.product?.family?.name || '';
    }
    const mainImage = getMainImageFromOffer(promotion.offer, promotion.imageNumber);
    if (!promotion.card.image?.src && mainImage) {
      if (!promotion.card.image) {
        promotion.card.image = {
          lazy: true,
          sizes: 'xs:100vw sm:100vw md:50vw xl:33vw',
          imageStyle: 'aspect-ratio: 1',
          src: mainImage.large,
          alt: mainImage.name,
        };
      } else {
        promotion.card.image.alt = mainImage.name;
        promotion.card.image.src = mainImage.large;
      }
    }

    const link = `/geraete/${promotion.offer.product?.slug}`;
    if (!promotion.card.href && promotion.offer.product?.slug) {
      promotion.card.href = link;
    }

    return promotion;
  });

/**
 * Loads all products by "articleNumber" provided in the "promotions"-Array.
 * Will return all given promotions with an available offer.
 */
export async function loadOffersForPromotions<P = Promotion, O = ListOffer>(
  promotions: Promotion<P>[],
  inStock = false,
  progress = true,
  onlyWithOffer = true,
  ignoreDate = false
): Promise<PromotionOffer<P, O>[]> {
  try {
    // get active Promotions
    const activePromotions = ignoreDate ? promotions : getActivePromotions(promotions);
    const filter: OfferFilterParams = {
      articleNumbers: activePromotions
        .map(({ articleNumber }: Promotion) => articleNumber)
        .filter(Boolean),
    };
    if (inStock) {
      filter.productAvailable = true;
    }

    const offersFromGoliath =
      (filter.articleNumbers?.length || 0) > 0
        ? await fetchOffers({ filter, includeAggregations: false }, progress)
        : undefined;

    const responsePromotions: PromotionOffer<P, O>[] = [];
    activePromotions.forEach((productTeaser) => {
      const offer = offersFromGoliath?.entries?.find(
        (o) => o?.product?.articleNumber === productTeaser.articleNumber
      ) as O;
      if (offer) {
        responsePromotions.push({
          ...productTeaser,
          offer,
        });
      }
      // return all teasers without articleNumber.
      else if (!productTeaser.articleNumber || !onlyWithOffer || productTeaser.showWithoutOffer) {
        responsePromotions.push({
          ...productTeaser,
          offer: undefined,
        });
      }
    });
    return responsePromotions;
  } catch (e) {
    captureException(e);
  }
  return [];
}
